import { Alert, Stack } from '@mui/material'
import { useSnackbar } from 'notistack'
import { CostGroup, Costing, ExchangeData } from 'pdf-generator'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DataSheetPDFPreview, MyPaper } from '../../../components'
import { ReportConfig } from '../../../components/pdfBuilder/types'
import { useAppDispatch, useAppSelector } from '../../../redux'
import { useCalculateCostingMutation, useLazyGetAllCostingQuery, useLazyGetBOMTreeByIdQuery, useUpdatePricingMutation } from '../../../redux/services'
import { closeBackdrop, openBackdrop } from '../../../redux/slices/backdrop'
import { closeConfirmationDialog, openConfirmationDialog } from '../../../redux/slices/confirmationDialog'
import { addIdForAssembly, pricingOutdatedUpdateFn } from '../utils'

const Preview = ({ refetch, isFetched, footerURL, readOnly }: { refetch: () => void, isFetched: boolean, footerURL: string, readOnly: boolean }) => {

    const dispatch = useAppDispatch();
    const { id = "" } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [getAllCosting, costingRes] = useLazyGetAllCostingQuery();
    const [getBOMTree] = useLazyGetBOMTreeByIdQuery();
    const [calculateCost] = useCalculateCostingMutation();
    const [updatePricing] = useUpdatePricingMutation();

    const { pricing } = useAppSelector(store => store);

    const [currentCosting, setCosting] = useState<any>(null);

    const retchCosting = () => {
        getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id });
    }

    const onReportConfigChange = async (data: ReportConfig) => {
        dispatch(openBackdrop("Saving the pricing..."));
        const {
            basic_details,
            bom_cost,
            exchange_rates,
            cost_groups = [],
            other_costs = [],
            discounts = [],
            taxes = [],
            bom_cost_quantity = 1,
            bom_cost_discount_percentage = 0,
            margin_ratio = 0,
        } = pricing;

        await updatePricing({
            pricing_id: id,
            bom_id: basic_details?.bom_id?._id as string,
            payload: {
                bom_cost: addIdForAssembly(bom_cost), // TODO: To be taken up by backend
                pricing_name: basic_details.pricing_name,
                pricing_model: basic_details?.pricing_model?.value,
                remarks: basic_details?.remarks,
                exchange_rates: exchange_rates,
                margin_ratio,
                cost_groups: cost_groups?.map((c: CostGroup, idx: number) => ({
                    position: idx + 1,
                    title: c.title,
                    quantity: Number(c.quantity) || 1,
                    discount_percentage: Number(c.discount_percentage) || 0,
                    total_amount: Number(c.total_amount) || 0,
                    // value: c.value,
                    costs: c.costs?.map((cost, idx: number) => ({
                        position: idx + 1,
                        description: cost.description,
                        units: cost.units,
                        value: Number(cost.value) || 0,
                        quantity: Number(cost.quantity) || 0,
                        total_cost: Number(cost.total_cost) || 0
                    })) ?? []
                })) ?? [],
                other_costs: other_costs,
                discounts: discounts,
                taxes: taxes,
                bom_cost_quantity,
                bom_cost_discount_percentage,
                // links: [{ object_type: getObjectType(location) as string, object_ids: [p_id] }],
                report_config: data
            }
        })
            .unwrap()
            .then((res) => {
                onCalculate(exchange_rates);
                refetch();
            })
            .catch(async (error) => {
                enqueueSnackbar(error?.data?.title ?? "Unable to update the pricing", { variant: "error" });
                console.error(`Error updating pricing: ${JSON.stringify(error)}`)
            })
            .finally(() => {
                dispatch(closeBackdrop());
            })
    }

    const onCalculate = async (exchange_rates: ExchangeData) => {
        dispatch(openBackdrop("Calculating Cost..."));
        await calculateCost({ pricing_id: id, exchange_rates: exchange_rates })
            .unwrap()
            .then((res) => {
                getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id });
            }).catch((error) => {
                setCosting(null);
                if (error?.data?.title === "BOM Outdated" && error?.data?.status === 409) {
                    dispatch(openConfirmationDialog({
                        title: "Pricing Update",
                        body: "The BOM of this pricing is updated, do you want to update the pricing with latest BOM changes ?",
                        negativeBtn: "Cancel",
                        positiveBtn: "Yes",
                        onOk: () => {
                            pricingOutdatedUpdateFn(getBOMTree, updatePricing, dispatch, pricing, id, () => {
                                retchCosting();
                                refetch();
                            });
                        },
                        onNegativeBtn: () => dispatch(closeConfirmationDialog())
                    }))
                }
                enqueueSnackbar(error?.data?.title ?? "Unable to calculate the pricing", { variant: "error" });
                console.error(`Error calculating pricing: ${JSON.stringify(error)}`)
            }).finally(() => {
                dispatch(closeBackdrop());
            });
    }

    useEffect(() => {
        if (costingRes.isSuccess) {
            dispatch(closeBackdrop());
            const { data = [] } = costingRes;
            const latestCosting = data?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
            const mostRecentCosting: Costing = latestCosting?.reduce((recent: any, current: any) => {
                return new Date(current.created_time) > new Date(recent.created_time) ? current : recent;
            }, latestCosting?.[0] || {});
            if (mostRecentCosting && Object.keys(mostRecentCosting)?.length > 0) {
                setCosting(mostRecentCosting);
            }

        }
        if (costingRes.isError) {
            enqueueSnackbar("Something went wrong, Unable to fetch the calculated cost", { variant: "error" });
            dispatch(closeBackdrop());
        }
        // eslint-disable-next-line
    }, [costingRes.status]);

    useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (isFetched && pricing.basic_details?.bom_id?.value) {
            dispatch(openBackdrop("Fetching pricing..."))
            getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id });
        }
        // eslint-disable-next-line
    }, [isFetched, pricing.basic_details?.bom_id?.value])

    return (
        (<Stack sx={{
            height: "90vh"
        }}>
            {costingRes.isError && <MyPaper><Alert severity="error">Something went wrong, Unable to fetch costing details</Alert></MyPaper>}
            {costingRes.isSuccess && ((costingRes?.data ?? []).length === 0 || Object.keys(currentCosting || {}).length <= 0) && <MyPaper><Alert severity="info">You are yet to calculate the costing details for this BOM.</Alert></MyPaper>}
            {costingRes.isSuccess && Object.keys(currentCosting || {}).length > 0 &&
                <>
                    <DataSheetPDFPreview
                        title={"Master Pricing Summary"}
                        headerTitle={`Master Pricing Summary`}
                        footerURL={footerURL}
                        type={"costbreakupreport"}
                        basicDetails={currentCosting}
                        pricingID={pricing.sequenceId ?? ""}
                        released={(readOnly || pricing?.released) ?? false}
                        pdfConfig={{
                            report_config: pricing.report_config as ReportConfig,
                            onReportConfigChange
                        }}
                    />
                </>
            }
        </Stack>)
    );
}

export default Preview